import React, { useState } from "react";
import axios from "axios";
import exif from "exif-js";

import "./App.css";
import "leaflet/dist/leaflet.css";

import UploadedImage from "./components/Image";
import Map from "./components/Map";

import { useTranslation } from "react-i18next";

const defaultCoordinates = [63.430477, 10.3943063];

const lngs = {
  en: { nativeName: "English" },
  no: { nativeName: "Norsk" },
};

function App() {
  const { t, i18n } = useTranslation();

  const [images, setImages] = useState([]);
  const [imgCoordinates, setImgCoordinates] = useState([]);
  const [mapCoordinate, setMapCoordinate] = useState(defaultCoordinates);
  const [mapUncertianty, setMapUncertainty] = useState(15);
  const [wizardStep, setWizardStep] = useState(1);

  const [art, setArt] = useState("");
  const [artSikkerhet, setArtSikkerhet] = useState(1);
  const [objectSize, setObjectSize] = useState("");
  const [objectDefinition, setObjectDefinition] = useState("");

  const [email, setEmail] = useState("");
  const [comments, setComments] = useState("");

  document.addEventListener("backbutton", onBackKeyDown, false);

  function onBackKeyDown() {
    // Handle the back button
  }

  const addImage = async (uploaded) => {
    setImages([...images, ...uploaded]);

    for (let i of uploaded) {
      await exif.getData(i, function () {
        const exifData = exif.getAllTags(this);

        if (!exifData.GPSLatitude || !exifData.GPSLongitude) {
          return;
        }

        let latSign = exifData.GPSLatitudeRef === "N" ? 1 : -1;
        let lngSign = exifData.GPSLongitudeRef === "E" ? 1 : -1;

        let coordinates = [
          latSign * exifData.GPSLatitude[0] +
            exifData.GPSLatitude[1] / 60 +
            exifData.GPSLatitude[2] / 3600,
          lngSign * exifData.GPSLongitude[0] +
            exifData.GPSLongitude[1] / 60 +
            exifData.GPSLongitude[2] / 3600,
        ];

        let avgCoordinates = [...imgCoordinates, coordinates].reduce(
          (acc, cur) => {
            return [
              acc[0] + cur[0] / [...imgCoordinates, coordinates].length,
              acc[1] + cur[1] / [...imgCoordinates, coordinates].length,
            ];
          },
          [0, 0]
        );

        setMapCoordinate(avgCoordinates);
        setImgCoordinates([...imgCoordinates, coordinates]);
      });
    }
  };

  const deleteImage = (index) => {
    const confirmDeletion = window.confirm(t("actions.confirmDeletion"));

    if (confirmDeletion) {
      let newImages = images;
      newImages.splice(index, 1);
      setImages([...newImages]);

      let newCoordinates = imgCoordinates;
      newCoordinates.splice(index, 1);
      setImgCoordinates([...newCoordinates]);

      let avgCoordinates = newCoordinates.reduce(
        (acc, cur) => {
          return [
            acc[0] + cur[0] / newCoordinates.length,
            acc[1] + cur[1] / newCoordinates.length,
          ];
        },
        [0, 0]
      );

      setMapCoordinate(avgCoordinates);
    }
  };

  const report = () => {
    setWizardStep(4);
    let formData = new FormData();

    for (let i of images) {
      formData.append("images", i);
    }

    formData.append("lat", mapCoordinate[0]);
    formData.append("lng", mapCoordinate[1]);
    formData.append("uncertainty", mapUncertianty);
    formData.append("art", art);
    formData.append("artSikkerhet", artSikkerhet);
    formData.append("objectDefinition", objectDefinition);
    formData.append("objectSize", objectSize);
    formData.append("email", email);
    formData.append("comments", comments);

    axios
      .post("https://fpbackend.vm.ntnu.no", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        // alert("Rapporten ble sendt inn!");
        setWizardStep(5);
      })
      .catch((error) => {
        // alert("Noe gikk galt, prøv igjen senere.");
        console.log(error);
      });
  };

  const reset = () => {
    setImages([]);
    setImgCoordinates([]);
    setMapCoordinate(defaultCoordinates);
    setMapUncertainty(15);
    setWizardStep(1);
    setArt("");
    setArtSikkerhet(1);
    setComments("");
  };

  const goToInput = () => {
    document.getElementById("uploaderImages").click();
  };

  const setLat = (e) => {
    if (e.target.value > 90 || e.target.value < -90 || isNaN(e.target.value)) {
      return;
    }

    setMapCoordinate([e.target.value, mapCoordinate[1]]);
  };

  const setLon = (e) => {
    if (
      e.target.value > 180 ||
      e.target.value < -180 ||
      isNaN(e.target.value)
    ) {
      return;
    }
    setMapCoordinate([mapCoordinate[0], e.target.value]);
  };

  const uploadMore = async (sender) => {
    await addImage(document.getElementById(sender).files);
    document.getElementById(sender).value = "";
  };

  const handleSliderChange = (event) => {
    setMapUncertainty(event.target.value);
  };

  const sikkerheter = [
    t("certainties.veryUncertain"),
    t("certainties.notSoCertain"),
    t("certainties.quiteCertain"),
    t("certainties.veryCertain"),
  ];

  t("certainties");

  return (
    <React.Fragment>
      <div>
        {Object.keys(lngs).map((lng) => (
          <button
            key={lng}
            style={{
              fontWeight: i18n.resolvedLanguage === lng ? "bold" : "normal",
            }}
            type="submit"
            onClick={() => i18n.changeLanguage(lng)}
          >
            {lngs[lng].nativeName}
          </button>
        ))}
      </div>

      {wizardStep === 1 && (
        <>
          <h1>{t("heading.uploadPictures")}</h1>
          <p className="instructions">
            {t("instructions.uploadPictures.general")}
          </p>
          <p className="instructions">
            {t("instructions.uploadPictures.size")}
          </p>

          <div className={"images scrollbarless"}>
            <div className="goToInput" onClick={goToInput}></div>
            {images.map((img, index) => (
              <UploadedImage
                img={img}
                key={index}
                imgIndex={index}
                deleteImage={deleteImage}
              />
            ))}
          </div>
        </>
      )}

      {wizardStep === 2 && (
        <>
          <h1>{t("heading.where")}</h1>
          <p className="instructions">{t("instructions.where.general")}</p>

          <p className="inputs">
            <label htmlFor="coordinates">
              {t("formField.coordinates.label")}
            </label>
            <span className="inputFields">
              <input
                type="text"
                id="latBox"
                value={mapCoordinate[0]}
                onChange={(e) => setLat(e)}
              />

              <input
                type="text"
                id="lonBox"
                value={mapCoordinate[1]}
                onChange={(e) => setLon(e)}
              />
            </span>
          </p>

          <Map
            lat={mapCoordinate[0]}
            lng={mapCoordinate[1]}
            uncertainty={mapUncertianty}
            setMapCoordinate={setMapCoordinate}
          />

          <input
            type="range"
            min="5"
            max="5000"
            value={mapUncertianty}
            onChange={handleSliderChange}
            className="slider"
          />
          <p className="instructions">
            {t("formField.mapUncertainty.label")}: {mapUncertianty} m
          </p>
        </>
      )}

      {wizardStep === 3 && (
        <>
          <h1>{t("heading.otherData")}</h1>
          <p className="instructions">{t("instructions.otherData.general")}</p>

          <p className="inputs">
            <label htmlFor="art">{t("formField.species.label")}:</label>
            <input
              type="text"
              id="art"
              value={art}
              onChange={(e) => setArt(e.target.value)}
              placeholder={t("formField.species.placeholder")}
            />
          </p>

          <p className="inputs">
            <label htmlFor="art">
              {t("formField.speciesCertainty.label")}:
            </label>
            <input
              type="range"
              min="1"
              max="4"
              className="slider"
              value={artSikkerhet}
              onChange={(e) => setArtSikkerhet(e.target.valueAsNumber)}
            />
          </p>
          <p className="inputs" style={{ height: 0, padding: 0 }}>
            <span className="sliderValue">{sikkerheter[artSikkerhet - 1]}</span>
          </p>

          <p className="inputs">
            <label htmlFor="objectDefinition">
              {t("formField.objectDefinition.label")}:
            </label>
            <input
              type="text"
              id="objectDefinition"
              placeholder={t("formField.objectDefinition.placeholder")}
              value={objectDefinition}
              onChange={(e) => setObjectDefinition(e.target.value)}
            />
          </p>

          <p className="inputs">
            <label htmlFor="objectSize">
              {t("formField.objectSize.label")}:
            </label>
            <input
              type="text"
              id="objectSize"
              placeholder={t("formField.objectSize.placeholder")}
              value={objectSize}
              onChange={(e) => setObjectSize(e.target.value)}
            />
          </p>

          <p className="inputs">
            <label htmlFor="email">{t("formField.email.label")}:</label>
            <input
              type="email"
              id="email"
              placeholder={t("formField.email.placeholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </p>

          <p className="inputs">
            <label htmlFor="kommentarer">
              {t("formField.comments.label")}:
            </label>
            <textarea
              id="kommentarer"
              placeholder={t("formField.comments.placeholder")}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </p>
        </>
      )}

      {wizardStep === 4 && (
        <>
          <h2>{t("messages.uploading")}</h2>
          <h3>{t("messages.pleaseWait")}</h3>
        </>
      )}

      {wizardStep === 5 && <h1>{t("messages.thanks")}</h1>}


      <div className="spacer" />

      <div className="buttons">
        {wizardStep > 1 && wizardStep < 4 && (
          <button className="btn" onClick={() => setWizardStep(wizardStep - 1)}>
            ⬅ {t("actions.back")}
          </button>
        )}

        {images.length > 0 && wizardStep < 3 && (
          <button className="btn" onClick={() => setWizardStep(wizardStep + 1)}>
            {t("actions.continue")} ➡
          </button>
        )}

        {wizardStep === 3 && (
          <button className="btn" onClick={() => report()}>
            {wizardStep === 4 ? t("messages.uploading") : t("actions.send")}
          </button>
        )}

        {wizardStep === 5 && (
          <button className="btn" onClick={() => reset()}>
            {t("actions.backToStart")}
          </button>
        )}
      </div>
      <input
        className="clickable"
        type="file"
        id="uploaderImages"
        multiple
        onChange={uploadMore.bind(this, "uploaderImages")}
        style={{ display: "none" }}
      />
    </React.Fragment>
  );
}

export default App;
